/**
 * This file is extracted from edm-odata for reuse.
 * Original path: private\client-data\packages\edm-odata\src\server-call-cache-item.js
 */
import { noop } from 'underscore';
import Promise from 'bluebird';
import { Observable } from './observable';
import {
  ICallCacheState, ICallObservable, ICallObservableOptions, ICallFunction,
  IObserver,
  ISubscriptionObserver,
} from './types';

/**
 * make a server call and setup the cache item state
 * @param {Function} call function returns a Promise of data
 * @returns {Promise} a resolved promise
 */
function makeCall<T = unknown, E = unknown>(call: ICallFunction<T>): Promise<null | void> {
  const self = this as ICallObservable<T, E>;

  self.state.status = 'PENDING';
  return call()
    .then((data: T): null => {
      self.setData(data);

      return null;
    })
    .catch((err: E): void => {
      self.observers.forEach((item) => {
        item.error(err);
      });
      self.state.status = 'ERROR';
      self.state.error = err;
    });
}

export class CallObservable<T = unknown, E = unknown> extends Observable<T, E> implements ICallObservable<T, E> {
  afterSubscribe: NonNullable<ICallObservableOptions<T, E>['afterSubscribe']>;

  afterUnsubscribe: NonNullable<ICallObservableOptions<T, E>['afterUnsubscribe']>;

  fetch: ICallFunction<T>;

  observers: Set<IObserver<T, E>>;

  state: ICallCacheState<T, E>;

  constructor({
    fetch,
    afterSubscribe = noop,
    afterUnsubscribe = noop,
    hardResetCacheData,
  }: ICallObservableOptions<T, E>) {
    super((observer: ISubscriptionObserver<T, E>) => {
      this.observers.add(observer);
      this.afterSubscribe(this);
      if (this.state.status === 'WAITING') {
        if (hardResetCacheData) {
          this.setData(hardResetCacheData);
        } else {
          this.invalidate();
        }
      } else if (this.state.status === 'READY') {
        observer.next(this.state.data);
      } else if (this.state.status === 'ERROR') {
        observer.error(this.state.error);
      }
      return () => {
        this.observers.delete(observer);
        this.afterUnsubscribe(this);
      };
    });
    this.fetch = fetch;
    // eslint-disable-next-line no-undef
    this.observers = new Set<IObserver<T, E>>();
    this.afterSubscribe = afterSubscribe;
    this.afterUnsubscribe = afterUnsubscribe;
    this.state = {
      // WAITING - before any subscription
      // PENDING - has subscription, call made but not responded
      // READY - already has data
      // ERROR - already has error
      status: 'WAITING',
      data: null,
      error: null,
    };
  }

  setData(data: T): null {
    this.observers.forEach((item) => {
      item.next(data);
    });
    this.state.status = 'READY';
    this.state.data = data;

    return null;
  }

  invalidate(): void {
    if (this.observers.size === 0) {
      this.state.status = 'WAITING';
    } else {
      makeCall.call(this, () => this.fetch());
    }
  }

  hasObservers(): boolean {
    return this.observers.size > 0;
  }
}
